import { useState } from 'react';
import { PrivacyInfo } from './PrivacyInfo';
import { Drawer } from './Drawer';

/**
 * About / methodology panel. Mirrors SettingsPanel's collapsed-button →
 * expandable-section pattern so the header stays uncluttered. Pure
 * presentational content: explains, in the editor's language, exactly what
 * the tool does, how it reaches each result, how to read those results, and
 * — just as importantly — what it deliberately does NOT do. Transparency is
 * a product requirement, not a nicety: an editor must be able to trust and
 * audit every signal before it informs a decision on someone's manuscript.
 */
export function AboutPanel() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ms-btn ms-btn-secondary ms-btn-xs"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        About
      </button>

      <Drawer open={open} onClose={() => setOpen(false)} title="About ManuscriptSkim">
      <p className="text-xs text-slate-700">
        ManuscriptSkim is a <span className="font-medium">decision-support</span> tool for
        editorial board members. It performs the fast, mechanical first-pass triage an editor
        does before deciding whether a submission is worth a referee&rsquo;s time — surfacing
        reporting gaps, internal inconsistencies, and reviewer candidates — so your attention
        goes to judgement, not bookkeeping. It is <span className="font-medium">not</span> an
        automated reviewer and never decides anything: every output is a prompt for you to look,
        not a verdict.
      </p>

      <div className="mt-3 space-y-2">
        {/* ---- 1. The method ---- */}
        <details className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5" open>
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            How a manuscript is analysed
          </summary>
          <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-slate-700">
            <p>
              Analysis is <span className="font-medium">deterministic first</span>: the core
              engine is rule-based, so the same manuscript always yields the same result and
              every finding can be traced to the exact passage that triggered it. An optional
              language model adds <em>explanatory</em> colour only — it can never create or
              suppress a finding, and the tool is fully functional with it switched off. That
              model runs <span className="font-medium">on-device by default</span> (nothing
              leaves your machine). A separate, off-by-default option lets you switch it to
              Google Gemini using your own API key — in <em>that</em> mode only, manuscript
              excerpts are sent to Google; you are warned before enabling it.
            </p>
            <ol className="ml-4 list-decimal space-y-1">
              <li>
                <span className="font-medium">Text extraction.</span> The PDF is read in your
                browser (pdf.js). No file is uploaded.
              </li>
              <li>
                <span className="font-medium">Submission-system clean-up.</span> Editorial
                Manager cover sheets are parsed for authoritative metadata (title, authors,
                article type, author keywords); a duplicated body copy is collapsed; running
                heads/footers, page numbers and line-number gutters are removed only when
                confidently detected — otherwise the text is left untouched.
              </li>
              <li>
                <span className="font-medium">Structure detection.</span> Sections (Abstract,
                Methods, Results, Discussion, References&hellip;) and the title are located so
                later checks know <em>where</em> a number or claim sits.
              </li>
              <li>
                <span className="font-medium">Study-type classification.</span> Cues across the
                manuscript are weighed to identify the design (RCT, systematic review, cohort,
                case report&hellip;) and map it to the matching EQUATOR reporting guideline.
              </li>
              <li>
                <span className="font-medium">Reporting checklist.</span> The manuscript is
                checked for the methodological elements that the guideline for{' '}
                <em>that</em> design expects.
              </li>
              <li>
                <span className="font-medium">Numeric &amp; consistency checks.</span> Sample
                sizes are compared across Abstract/Methods/Results; p-value reporting
                completeness and abstract &ldquo;spin&rdquo; phrasing are checked. Numbers
                inside tables and subgroup counts are deliberately excluded to avoid false
                alarms.
              </li>
              <li>
                <span className="font-medium">Reference audit.</span> References are parsed and,
                where a DOI is present, resolved via Crossref to flag retractions and metadata
                mismatches.
              </li>
              <li>
                <span className="font-medium">Reviewer suggestions.</span> The
                author&rsquo;s own declared keywords lead a topic query to OpenAlex and PubMed;
                a conflict-of-interest filter removes the manuscript&rsquo;s own authors and
                institutions.
              </li>
              <li>
                <span className="font-medium">Decision rubric &amp; export.</span> Findings are
                summarised into an <em>advisory</em> recommendation with its reasoning shown,
                and can be exported as email, PDF or JSON.
              </li>
            </ol>
          </div>
        </details>

        {/* ---- 2. Reading the results ---- */}
        <details className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            How to read the results
          </summary>
          <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-slate-700">
            <p>
              <span className="rounded bg-red-100 px-1 font-medium text-red-800">RED</span>{' '}
              &mdash; a likely material problem (e.g. a sample size that disagrees across
              sections by a wide margin). Worth resolving before the manuscript goes to
              reviewers.
            </p>
            <p>
              <span className="rounded bg-amber-100 px-1 font-medium text-amber-900">
                AMBER
              </span>{' '}
              &mdash; a possible gap or imprecision (e.g. an inexact p-value, a missing
              checklist item). A point to check, often legitimately explained in the full text.
            </p>
            <p>
              Every finding cites the passage that produced it.{' '}
              <span className="font-medium">
                A flag is an invitation to look at that passage, not a judgement on the work.
              </span>{' '}
              The engine is deliberately tuned so that a missed nuance is preferable to a false
              alarm — if anything, it under-flags.
            </p>
            <p>
              <span className="font-medium">Study type</span> shows a confidence level. Low
              confidence means &ldquo;please confirm the design&rdquo;, not that the work is
              flawed. If no formal reporting guideline is encoded for a design, the tool says so
              explicitly and names the one that would apply rather than silently applying none.
            </p>
            <p>
              <span className="font-medium">The recommendation</span> (send for review / revise
              / reject) is a rubric summary of the findings with its basis spelled out. It is a
              starting point for your judgement, never a substitute for it.
            </p>
          </div>
        </details>

        {/* ---- 3. For editors ---- */}
        <details className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            For editors: how to use this
          </summary>
          <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-slate-700">
            <ul className="ml-4 list-disc space-y-1">
              <li>
                Use it as a <span className="font-medium">pre-screen</span>: run it, scan the
                RED/AMBER findings against the cited passages, then apply your editorial
                judgement.
              </li>
              <li>
                Treat findings as <span className="font-medium">questions to verify</span>, not
                conclusions. The tool cannot read intent, novelty, clinical importance, or
                field-specific convention — that is your role.
              </li>
              <li>
                You can add or dismiss reviewer candidates; your selections and dismissals are
                recorded locally so a later session restores your work.
              </li>
              <li>
                Nothing is auto-sent. Email/PDF/JSON exports are generated for{' '}
                <span className="font-medium">you</span> to review and send.
              </li>
              <li>
                <span className="font-medium">Confidentiality:</span> by default the manuscript
                stays on your device. The <em>only</em> exception is if you deliberately switch
                the AI engine to cloud (Gemini) in Settings, which sends excerpts to Google
                (you are warned first). Either way, follow your journal&rsquo;s confidentiality
                policy as you would for any submission.
              </li>
            </ul>
          </div>
        </details>

        {/* ---- 4. Accuracy & transparency ---- */}
        <details className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            Accuracy, limits &amp; transparency
          </summary>
          <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-slate-700">
            <p>
              <span className="font-medium">Reproducible.</span> The deterministic engine is
              versioned and a content hash of the analysed text is recorded, so a result can be
              regenerated and audited. Key actions are written to a local audit log.
            </p>
            <p>
              <span className="font-medium">False-positive discipline.</span> Heuristics that
              proved error-prone on real manuscripts (e.g. reading a power-calculation target
              or a table cell as the study&rsquo;s sample size) were narrowed or disabled rather
              than left to over-flag. Where a check is only safe with full-text understanding,
              it is routed to the optional model or deferred — not emitted as a hard flag.
            </p>
            <p>
              <span className="font-medium">Known limits.</span> It does not assess scientific
              validity, statistical correctness beyond simple consistency, image integrity, or
              plagiarism. Reporting-guideline coverage is partial and clearly indicated.
              Extraction quality depends on the PDF; scanned/image-only files yield little and
              the tool will say so.
            </p>
          </div>
        </details>

        {/* ---- 5. Privacy (single source of truth) ---- */}
        <details className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            Privacy &amp; network
          </summary>
          <div className="mt-2">
            <PrivacyInfo />
          </div>
        </details>
      </div>

      <p className="mt-3 text-[10px] text-slate-500">
        Decision-support only. ManuscriptSkim assists editorial triage; it does not replace peer
        review or editorial judgement, and the editor remains responsible for every decision.
      </p>
      </Drawer>
    </>
  );
}
